import fs from 'fs-extra'
import path from 'path'

import { type InitOptions } from '@commands/init'
import { txt } from '@utils/colors'
import { getProjectType } from '@utils/getProjectInfo'
import { spinner } from '@utils/spinner'

/**
 * Runs preflight checks before project initialization.
 * - Ensures valid project structure.
 * - Prevents overwriting existing `components.json`.
 * - Detects framework and verifies Tailwind setup.
 * - Checks TypeScript import alias configuration.
 */
export const preFlightInit = async (options: InitOptions) => {
  // Check for empty project. We assume if no package.json exists, the project is empty.

  if (!fs.existsSync(options.cwd) || !fs.existsSync(path.resolve(options.cwd, 'package.json'))) {
    return {
      projectInfo: null,
    }
  }

  const beforeInitCheckSpinner = spinner('Preflight checks', {
    silent: false,
  }).start()

  if (fs.existsSync(path.resolve(options.cwd, 'components.json'))) {
    beforeInitCheckSpinner.fail()
    throw new Error(`The ${txt.info('components.json')} file already exists at ${txt.info(options.cwd)}.
        \nTo start over, remove the ${txt.info('components.json')} file and run ${txt.info('init')} command again`)
  }

  beforeInitCheckSpinner.succeed('Before init checks completed')

  const frameworkSpinner = spinner('Detecting framework', {
    silent: false,
  }).start()

  const projectInfo = await getProjectType(options.cwd)

  if (!projectInfo || projectInfo?.framework.name === 'manual') {
    frameworkSpinner?.fail()
    if (projectInfo?.framework.link.installation) {
      throw new Error(
        `We could not detect a supported framework at ${txt.info(options.cwd)}.\n` +
          `Visit ${txt.info(
            projectInfo?.framework.link.installation
          )} to manually configure your project.\nOnce configured, you can use the cli to add components.`
      )
    }
  }
  frameworkSpinner?.succeed(`Detecting framework. Detected ${txt.info(projectInfo.framework.label)}.`)

  const tailwindSpinner = spinner('Verifying tailwind configuration', {
    silent: false,
  }).start()

  if (!projectInfo.tailwindCssFile) {
    tailwindSpinner?.fail()
    throw new Error(
      `We could not find a Tailwind  at ${txt.info(options.cwd)}.\n` +
        `Make sure you have a Tailwind installed your project.\n` +
        `Visit ${txt.info('https://tailwindcss.com/docs/installation')} to get started.`
    )
  }
  tailwindSpinner?.succeed(`Verifying tailwind configuration. Found Tailwind configuration.`)

  const tsConfigSpinner = spinner('Validating import alias', {
    silent: false,
  }).start()
  if (!projectInfo.aliasPrefix) {
    tsConfigSpinner.fail()
    throw new Error(
      `We could not find an import alias prefix in your TypeScript configuration.\n` +
        `Make sure you have a 'paths' configuration in your tsconfig.json or tsconfig.js file.\n` +
        `Once configured, you can use the cli to add components.`
    )
  }
  tsConfigSpinner.succeed(`Validating import alias. Found import alias prefix: ${txt.info(projectInfo.aliasPrefix)}.`)

  return { projectInfo }
}
